import { Avatar, AvatarFallback, AvatarImage } from "@choose/ui/avatar";
import { Button } from "@choose/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@choose/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Skeleton } from "@choose/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { EllipsisIcon, SearchSlashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  SupportRole,
  useAssignSupportRoleToInternalUserMutation,
  useCurrentUserQuery,
  useInternalUsersQuery,
} from "~/graphql/graphql.gen";
import { getRoles } from "~/stores/roles-store";
import type { UsersFilters } from "~/stores/users-store";
import { filterUsers, sortUsers } from "~/stores/users-store";

export function MemberList({ filters }: { filters: UsersFilters }) {
  const { t } = useTranslation();
  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });
  const queryClient = useQueryClient();

  const { data: users, isLoading } = useInternalUsersQuery(
    {
      filters: {
        role: {
          in: [
            SupportRole.SuperAdmin,
            SupportRole.Admin,
            SupportRole.Agent,
            SupportRole.Market,
            SupportRole.OperatorManager,
            SupportRole.Emerch,
          ],
        },
      },
    },
    {
      select: (data) => data.internalUsers,
    },
  );

  const { mutateAsync } = useAssignSupportRoleToInternalUserMutation();

  if (isLoading) {
    return (
      <ul>
        {Array.from({ length: 5 }).map((_, index) => (
          <li key={index} className="border-b p-4 last:border-0">
            <div className="flex items-center gap-3">
              <div className="flex flex-1 gap-3">
                <Skeleton className="size-10" />
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-5 min-w-20" />
                  <Skeleton className="h-4 min-w-20" />
                </div>
              </div>

              <Skeleton className="h-3 w-12" />

              <Skeleton className="h-3 w-8" />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  const roles = getRoles(t);

  const members = users ?? [];

  const filteredMembers = filterUsers(members, filters);

  const rows = filteredMembers.toSorted(sortUsers);

  const canEditMembers =
    user?.__typename === "ActorInternalUser" &&
    (user.internalUser.supportRole === SupportRole.SuperAdmin ||
      user.internalUser.supportRole === SupportRole.Admin);
  const canDisableMembers =
    user?.__typename === "ActorInternalUser" &&
    (user.internalUser.supportRole === SupportRole.SuperAdmin ||
      user.internalUser.supportRole === SupportRole.Admin);

  if (rows.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <SearchSlashIcon />
          </EmptyMedia>
          <EmptyTitle>{t("members.empty.title")}</EmptyTitle>
          <EmptyDescription>{t("members.empty.description")}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <ul className="rounded-xl border">
      {rows.map((agent) => (
        <li key={agent.id} className="border-b p-4 last:border-0">
          <div className="flex items-center gap-3">
            <div className="flex flex-1 items-center gap-3 truncate">
              <Avatar size="lg">
                <AvatarImage src={agent.internalUser.picture} />
                <AvatarFallback>
                  {agent.internalUser.firstName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col truncate">
                <span className="truncate">
                  {agent.internalUser.firstName} {agent.internalUser.lastName}
                </span>
                <span className="text-muted-foreground truncate text-sm">
                  {agent.internalUser.email}
                </span>
              </div>
            </div>
            <div className="text-muted-foreground text-sm">
              {
                roles.find(
                  (role) => role.value === agent.internalUser.supportRole,
                )?.label
              }
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="ghost" size="sm">
                    <EllipsisIcon />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="min-w-48">
                <DropdownMenuItem
                  className="gap-2"
                  render={
                    <Link
                      to="/settings/member/$memberId"
                      params={{ memberId: agent.id }}
                    >
                      {t("settings.members.edit_role")}
                    </Link>
                  }
                  disabled={!canEditMembers}
                />
                <DropdownMenuItem
                  variant="destructive"
                  className="gap-2"
                  onClick={async () => {
                    try {
                      await mutateAsync({
                        actorId: agent.id,
                        role: SupportRole.Viewer,
                      });
                      void queryClient.invalidateQueries({
                        queryKey: useInternalUsersQuery.getKey(),
                      });
                      void queryClient.invalidateQueries({
                        queryKey: useInternalUsersQuery.getKey(),
                      });
                    } catch {
                      toast.error(t("settings.members.disable_member.error"));
                    }
                  }}
                  disabled={!canDisableMembers}
                >
                  {t("settings.members.disable_member.label")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </li>
      ))}
    </ul>
  );
}
